import { useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { fetchAddress } from "../api/publisherService";
import { ADDRESS_SUGGEST_DEBOUNCE_MS, isAddressSuggestQuery, } from "../utils/addressSuggestUtils";
/**
 * State of one address typeahead (stripes Selection with asyncFilter) over `publisher-address/suggest` (DB-876):
 * queries shorter than 3 characters never reach the backend, the request waits 300 ms after the last keystroke,
 * and only the response to the latest query settles the offered addresses.
 *
 * @param ky the okapi ky instance; its identity may change on every render
 * @returns the offered addresses (also settable by the form, e.g. the ARES address), the loading flag,
 *   the id of the message for an empty list and the onFilter handler for the Selection
 */
export var useAddressSuggest = function (ky) {
    var _a = useState([]), addresses = _a[0], setAddresses = _a[1];
    var _b = useState(""), query = _b[0], setQuery = _b[1];
    var _c = useState(false), loading = _c[0], setLoading = _c[1];
    var _d = useState(false), failed = _d[0], setFailed = _d[1];
    // id of the latest query typed in; only the response to it may settle addresses and loading
    var latestRequest = useRef(0);
    var kyRef = useRef(ky);
    kyRef.current = ky;
    // built once: a new ky identity must not cancel the debounce or orphan a running request
    var runSearch = useRef(debounce(function (text, request) {
        var settles = function () { return request === latestRequest.current; };
        fetchAddress(kyRef.current, text)
            .then(function (found) {
            if (settles()) {
                setAddresses(found !== null && found !== void 0 ? found : []);
            }
        })
            .catch(function () {
            if (settles()) {
                // the failure is shown in the typeahead (emptyMessageId)
                setAddresses([]);
                setFailed(true);
            }
        })
            .finally(function () {
            if (settles()) {
                setLoading(false);
            }
        });
    }, ADDRESS_SUGGEST_DEBOUNCE_MS)).current;
    useEffect(function () { return function () {
        runSearch.cancel();
        // ignore any response that arrives after unmount
        latestRequest.current += 1;
    }; }, [runSearch]);
    var onFilter = function (text) {
        var request = ++latestRequest.current;
        setQuery(text);
        setFailed(false);
        if (!isAddressSuggestQuery(text)) {
            runSearch.cancel();
            setAddresses([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        runSearch(text, request);
    };
    var emptyMessageId = "ui-br-publishers.address.suggest.minChars";
    if (failed) {
        emptyMessageId = "ui-br-publishers.address.suggest.error";
    }
    else if (isAddressSuggestQuery(query)) {
        emptyMessageId = "ui-br-publishers.address.suggest.noResults";
    }
    return { addresses: addresses, setAddresses: setAddresses, loading: loading, emptyMessageId: emptyMessageId, onFilter: onFilter };
};
